/**
 * GLOBAL `@keyframes` names -- the SSOT binding each animation's DEFINITION to every reference of it.
 *
 * WHY THIS LEAF EXISTS (DL-1.4, 2026-08-20). A CSS animation name is an unresolved free identifier: `animation: fadeIn
 * 120ms` against a keyframe nobody defined is not an error in CSS, in TypeScript, or in any bundler -- the declaration
 * is simply dropped and the element renders unanimated. Nothing reddens. Two live instances were found by hand:
 * `components/primitives/Modal.tsx` animated `fadeIn` and `slideIn`, neither of which existed anywhere in the repo, so
 * the modal and its backdrop had been appearing with no transition at all; and `components/common/StatusDot.tsx`
 * animated `pulse`, which only ever resolves where Tailwind's content scan happened to emit its `animate-pulse`
 * utility into the same bundle -- working by accident in the host app and silently dead in any SPA that does not use
 * that class.
 *
 * Routing both halves through these constants converts that silent class into a TypeScript error: the emitter in
 * `const/design-tokens-static-css.ts` interpolates these names into the `@keyframes` blocks it ships inside base.css,
 * and every consumer interpolates the same constant, so a name can no longer be defined under one spelling and
 * referenced under another. `QUAL-DANGLING-KEYFRAME-REFERENCE-001` covers the raw-string bypass this cannot reach.
 *
 * SCOPE IS THE GLOBAL SHEET ONLY. Keyframes declared inside a `*.module.css` are renamed per-module by the CSS-modules
 * transform and resolve against their own file, and an MFE that injects its own `<style>` block owns its names
 * outright; neither is reachable from here and neither belongs in this record.
 */

export const KEYFRAME_NAMES = {
	/** Continuous 360deg rotation for loading spinners. */
	SPIN: 'spin',
	/** Plain opacity 0 -> 1, no transform. What a scrim or backdrop wants. */
	FADE_IN: 'fadeIn',
	/** Opacity 0 -> 1 with a 4px upward drift; the `.fade-in` utility class binds this one. */
	FADE_IN_SUBTLE: 'fadeInSubtle',
	/** Entrance for a panel that should arrive from below: opacity plus an 8px upward translate. */
	SLIDE_IN: 'slideIn',
	// NO `SCALE_IN` MEMBER (DL-1.10, 2026-08-20). The `scaleIn` keyframe and its `.animate-scaleIn` utility were emitted
	// into every document with ZERO consumers on every channel -- no `className`, no `@apply`, no HTML -- so both were
	// cut from `const/design-tokens-static-css.ts`. `SLIDE_IN` is the entrance a centered surface takes today.
	/** Background-position sweep for skeleton loading states. */
	SHIMMER: 'shimmer',
	/** Two-stop opacity breathe for status indicators. */
	PULSE_DOT: 'pulse-dot',
} as const;

// NO DERIVED `KeyframeName` UNION (2026-08-20). One stood here and had ZERO readers on every channel -- not the
// emitter, not a consumer, not this leaf itself: every surface interpolates a `KEYFRAME_NAMES` MEMBER, so the value is
// what travels and the union type never had a position to occupy. Re-derive it the day a signature needs to accept
// "some keyframe name"; until then it is a name with no contract behind it.
